import PasswordInput from '../components/PasswordInput';
import AuthLayout from '../components/AuthLayout';
import Turnstile from '../components/Turnstile';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { trackSignUp } from '../lib/analytics';

const CompanyRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company_name: '',
    master_username: '',
    master_password: '',
    master_password_confirm: '',
    master_email: '',
    // ハニーポット。人間には見えないので、埋まっていれば自動投稿とみなす。
    website: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  // 認証メールが送れたか（#9）。失敗を成功として見せると、
  // マスターは email_verified が false のままログインできず、
  // 原因も分からないまま詰む。
  const [emailSent, setEmailSent] = useState(true);
  const [registeredUsername, setRegisteredUsername] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [resending, setResending] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.company_name || !formData.master_username || !formData.master_password || !formData.master_email) {
      setMessage('事業者名、ログインID、パスワード、メールアドレスは必須です');
      return;
    }

    if (formData.master_password !== formData.master_password_confirm) {
      setMessage('パスワードが一致しません');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const response = await axios.post('/api/companies/register', { ...formData, turnstileToken });

      const { company, master_account, email_sent } = response.data;
      const mailOk = email_sent !== false;

      setCompanyCode(company.company_code);
      setEmailSent(mailOk);
      setRegisteredUsername(master_account.username);

      // 獲得の主指標。どの流入がトライアル開始まで到達したかを見るために、
      // 登録が成功した時点でだけ送る（バリデーション落ちや失敗では送らない）。
      trackSignUp();
      setMessage(
        mailOk
          ? `事業者登録が完了しました！

${master_account.email} にメールアドレス確認メールを送信しました。
メールに記載されたリンクをクリックして、アカウントを有効化してください。

企業コードを必ず控えてください。`
          : `事業者登録は完了しました。企業コードを必ず控えてください。`
      );

      // フォームをクリア
      setFormData({
        company_name: '',
        master_username: '',
        master_password: '',
        master_password_confirm: '',
        master_email: '',
        website: '',
          });
    } catch (error: any) {
      setMessage(error.response?.data?.error || '事業者登録に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendMessage('');
    try {
      await axios.post('/api/auth/resend-verification-email', {
        companyCode,
        username: registeredUsername,
      });
      setResendMessage('確認メールを再送しました。メールをご確認ください。');
      setEmailSent(true);
    } catch (error: any) {
      setResendMessage(
        error.response?.data?.error ||
          '再送に失敗しました。時間をおいてお試しいただくか、info@kinlog.jp までご連絡ください。'
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthLayout title="事業者登録"
      description="14日間の無料トライアルをすぐに開始できます。クレジットカードの登録は不要です。">

      {message && (
        <div style={{
          padding: '15px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: message.includes('完了') ? '#d4edda' : '#f8d7da',
          color: message.includes('完了') ? '#155724' : '#721c24',
          border: `1px solid ${message.includes('完了') ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {message}
          {companyCode && (
            <div style={{ marginTop: '10px', fontWeight: 'bold', fontSize: '20px' }}>
              企業コード: {companyCode}
            </div>
          )}
        </div>
      )}

      {/* 認証メールを送れなかった場合の復旧導線（#9）。
          ここが無いと「登録できたのにログインできない」で終わる */}
      {companyCode && !emailSent && (
        <div
          style={{
            padding: '15px',
            marginBottom: '20px',
            borderRadius: '4px',
            backgroundColor: '#fff3cd',
            color: '#856404',
            border: '1px solid #ffeeba',
          }}
        >
          <strong>確認メールを送信できませんでした</strong>
          <p style={{ margin: '8px 0' }}>
            アカウントは作成されていますが、メールアドレスの確認が済むまでログインできません。
            下のボタンで再送してください。
          </p>
          <button
            onClick={handleResend}
            disabled={resending}
            style={{
              padding: '10px 16px',
              backgroundColor: '#856404',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: resending ? 'not-allowed' : 'pointer',
              opacity: resending ? 0.6 : 1,
            }}
          >
            {resending ? '送信中...' : '確認メールを再送する'}
          </button>
          {resendMessage && <p style={{ margin: '10px 0 0' }}>{resendMessage}</p>}
          <p style={{ margin: '10px 0 0', fontSize: '12px' }}>
            解決しない場合は info@kinlog.jp までご連絡ください。
          </p>
        </div>
      )}

      {companyCode ? (
        <div style={{ textAlign: 'center' }}>
          <p style={{ marginBottom: '20px' }}>
            この企業コードはログイン時に必要です。必ず控えてください。
          </p>
          <button
            onClick={() => navigate('/login')}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#2563EB',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            ログイン画面へ
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            事業者名 *
            </label>
            <input
              type="text"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px'
              }}
              disabled={isLoading}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              マスターアカウント ログインID *
            </label>
            <input
              type="text"
              name="master_username"
              value={formData.master_username}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px'
              }}
              disabled={isLoading}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              パスワード *
            </label>
            <PasswordInput
       
       name="master_password"
       value={formData.master_password}
       onChange={handleChange}
       style={{
        width: '100%',
        padding: '10px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        fontSize: '16px'
       }}
       disabled={isLoading}
      />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              パスワード（確認） *
            </label>
            <PasswordInput
       
       name="master_password_confirm"
       value={formData.master_password_confirm}
       onChange={handleChange}
       style={{
        width: '100%',
        padding: '10px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        fontSize: '16px'
       }}
       disabled={isLoading}
      />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              メールアドレス *
            </label>
            <input
              type="email"
              name="master_email"
              value={formData.master_email}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px'
              }}
              disabled={isLoading}
            />
            <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
              ※ 登録後、このメールアドレスに確認メールを送信します
            </small>
          </div>

          

          <Turnstile onToken={setTurnstileToken} />

          {/* ハニーポット。実在の入力欄ではないため見えない位置に置く。 */}
          <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden>
            <label htmlFor="website">Website</label>
            <input
              id="website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={formData.website}
              onChange={handleChange}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: isLoading ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            {isLoading ? '登録中...' : '事業者登録'}
          </button>

          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => navigate('/login')}
              style={{
                background: 'none',
                border: 'none',
                color: '#2563EB',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              既にアカウントをお持ちの方はこちら
            </button>
          </div>
        </form>
      )}
    </AuthLayout>
  );
};

export default CompanyRegisterPage;
